// src/pages/ServiceUpload.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE; // 🔥 replaced API_WRK for universal base

const serviceDetails = {
  Plumber: { description: "Install and repair pipes.", certification: "Required" },
  Electrician: { description: "Work with electrical systems.", certification: "Required" },
  "Lawn Cleaning": { description: "Lawn mowing and outdoor cleanup.", certification: "Not Required" },
  Cleaning: { description: "Indoor deep cleaning and housekeeping.", certification: "Not Required" },
  "Furniture Assembly": { description: "Assemble furniture items professionally.", certification: "Not Required" },
  "Roof Cleaning": { description: "Remove moss, debris, and buildup from rooftops.", certification: "Optional" },

  Barber: { description: "Haircuts and grooming services for men.", certification: "Required" },
  "Haircut & Styling (Women)": { description: "Haircuts, styling, and blowouts for women.", certification: "Required" },
  "Pedicure & Manicure": { description: "Professional nail grooming & treatments.", certification: "Required" },
  "Facial & Skin Care": { description: "Facial treatments & skincare routines.", certification: "Required" },
  "Threading / Waxing": { description: "Threading, waxing, and hair removal.", certification: "Optional" },
  "Makeup Artist (Event)": { description: "Event, party, and professional makeup.", certification: "Required" },
  "Bridal Makeup": { description: "Wedding day makeup and styling.", certification: "Required" },
  "At-Home Spa (Massage)": { description: "Home massage & relaxation services.", certification: "Required" },

  Moving: { description: "Assist with moving furniture and boxes.", certification: "Not Required" },
  "Child Care": { description: "Care and supervision for children.", certification: "Required" },
  Handyman: { description: "General home repairs & fixes.", certification: "Optional" },
  "Junk Removal": { description: "Remove junk or furniture (requires vehicle).", certification: "Not Required" },
  "Snow Removal": { description: "Clear snow from driveways and walkways.", certification: "Not Required" },
  "Window Cleaning": { description: "Professional interior/exterior window cleaning.", certification: "Optional" },

  // 🔥 New Jobs Added to Match Welcome.js
  Painter: { description: "Interior & exterior painting work.", certification: "Optional" },
  "Event Photographer": { description: "Event photography & videography.", certification: "Optional" },
  "Personal Chef": { description: "Cook meals for events or households.", certification: "Optional" },
  "Carpet Cleaning": { description: "Deep carpet steam cleaning.", certification: "Not Required" },
  "Pressure Washing": { description: "High-pressure outdoor cleaning.", certification: "Not Required" },
  "TV Mounting": { description: "Mount and set up televisions safely.", certification: "Not Required" },
  "Appliance Repair": { description: "Repair home appliances.", certification: "Optional" },
  "Flooring Repair": { description: "Repair wood, tile, laminate floors.", certification: "Optional" },
  "Pool / Hot Tub Cleaning": { description: "Clean and treat pool or hot tub water.", certification: "Optional" },

  // Extras
  Mechanic: { description: "Auto repair and diagnostics.", certification: "Required" },
  "Car Detailing": { description: "Interior and exterior car cleaning.", certification: "Not Required" },
  Driver: { description: "Professional driving service.", certification: "Required" },
  "Respite Worker": { description: "Assist individuals with special needs.", certification: "Required" },
  Mehndi: { description: "Professional mehndi/henna art.", certification: "Optional" },
  "Tutor / Teaching": { description: "Teach academic subjects or skills.", certification: "Required" },
};

const ServiceUpload = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const workerId = localStorage.getItem("workerId");
  const selectedServices = location.state?.selectedServices || [];

  const [certFiles, setCertFiles] = useState({});
  const [hasTruck, setHasTruck] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!workerId) {
      navigate("/worker-login"); // guard: no session
      return;
    }
    if (!selectedServices.length) {
      navigate("/select-services");
    }
  }, [workerId, selectedServices, navigate]);

  const sanitizeKey = (name) =>
    name.replace(/[^\w\s-]/gi, "").replace(/\s+/g, "_");

  const handleFileChange = (serviceName, file) => {
    const safeKey = sanitizeKey(serviceName);
    setCertFiles((prev) => ({ ...prev, [safeKey]: file }));
  };

  const handleSubmit = async () => {
    setError("");
    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("workerId", workerId);

      const servicesPayload = selectedServices.map((service) => ({
        name: service.name,
        hasTruck: service.name === "Moving" ? hasTruck : false,
      }));

      formData.append("services", JSON.stringify(servicesPayload));

      Object.keys(certFiles).forEach((key) => {
        formData.append(`certFiles[${key}]`, certFiles[key]);
      });

      const res = await axios.post(`${API_BASE}/api/worker/save-services`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const updatedStatus = res.data?.status || "incomplete";

      // ✅ Sync worker status into localStorage for App.js to read
      localStorage.setItem("workerStatus", updatedStatus);
      if (res.data?.worker?._id) {
        localStorage.setItem("workerId", res.data.worker._id);
      }

      // ✅ Navigate based on status
      if (updatedStatus === "incomplete") {
        navigate("/review-profile");
      } else if (["pending", "approved"].includes(updatedStatus)) {
        navigate("/dashboard");
      } else {
        navigate("/worker-login");
      }
    } catch (err) {
      console.error("❌ Upload failed:", err);
      setError(err?.response?.data?.error || "Failed to upload services. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const movingSelected = selectedServices.some((s) => s.name === "Moving");
  const canSubmit = !movingSelected || (movingSelected && hasTruck);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-slate-100 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <h2 className="text-2xl font-bold text-center text-indigo-700 mb-4">
          Upload Certificate / Reference / Experience Proof
        </h2>

        {selectedServices.map((serviceObj, idx) => {
          const name = serviceObj.name;
          const details =
            serviceDetails[name] || { description: "No description available.", certification: "Optional" };
          const safeKey = sanitizeKey(name);

          return (
            <div
              key={idx}
              className="bg-white border border-gray-200 rounded-xl shadow-sm p-5"
            >
              <div className="mb-3">
                <h3 className="text-lg font-semibold text-indigo-700">{name}</h3>
                <p className="text-sm text-gray-600">{details.description}</p>
                <p className="text-sm mt-1 text-gray-500">
                  Certification:{" "}
                  <span className="capitalize font-medium text-gray-700">
                    {details.certification}
                  </span>
                </p>
              </div>

              {details.certification !== "Not Required" && (
                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Upload Certificate / Reference / Proof of Experience
                  </label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => handleFileChange(name, e.target.files?.[0])}
                    className="w-full text-sm"
                  />
                  {certFiles[safeKey] && (
                    <p className="text-green-600 text-sm mt-1">
                      ✅ {certFiles[safeKey].name}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {movingSelected && (
          <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 p-4 rounded-xl">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                className="mr-2"
                checked={hasTruck}
                onChange={(e) => setHasTruck(e.target.checked)}
              />
              I confirm that I have a truck/vehicle suitable for moving jobs
            </label>
          </div>
        )}

        {error && <p className="text-red-500 text-center text-sm">{error}</p>}

        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          className={`w-full font-semibold py-3 rounded-xl transition text-white ${
            !canSubmit || submitting
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-indigo-600 hover:bg-indigo-700"
          }`}
        >
          {submitting ? "Submitting..." : "Submit Services"}
        </button>
      </div>
    </div>
  );
};

export default ServiceUpload;
